import { faArrowsRotate, faTrashCan } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useDocumentTitle } from "@uidotdev/usehooks";
import type { MobileDevice } from "../apiClient/generated";
import { LoadingIndicator } from "../components/MapV2/ui/LoadingIndicator";
import { useAuthApiClient } from "../hooks";
import { PrimaryButton, SecondaryButton } from "../ui/Buttons";

const DeviceRow = ({ device }: { device: MobileDevice }) => {
    const apiClient = useAuthApiClient();
    const queryClient = useQueryClient();

    const renewMutation = useMutation({
        mutationFn: async () => {
            return await apiClient.authMobileActivatePartialUpdate({
                deviceId: device.deviceId,
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["connectedDevices"] });
        },
    });

    const deleteMutation = useMutation({
        mutationFn: async () => {
            return await apiClient.authMobileActivateDestroy({
                deviceId: device.deviceId,
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["connectedDevices"] });
        },
    });

    const lastAuthorized = device.lastAuthorizedAt
        ? new Date(device.lastAuthorizedAt).toLocaleDateString()
        : "Never";
    const lastUsed = device.lastUsedAt
        ? new Date(device.lastUsedAt).toLocaleDateString()
        : "Never";

    const expiresAt = device.lastAuthorizedAt
        ? new Date(
              new Date(device.lastAuthorizedAt).getTime() +
                  7 * 24 * 60 * 60 * 1000,
          )
        : null;
    const isExpired = expiresAt ? expiresAt < new Date() : true;

    return (
        <div className="w-full px-6 py-4 bg-white rounded shadow border border-gray-300 flex items-center justify-between gap-4">
            <div className="flex flex-col gap-1 min-w-0">
                <div className="text-stone-900 font-semibold leading-6 truncate">
                    {device.deviceName || "Unnamed Device"}
                </div>
                <div className="flex items-center gap-3 text-sm text-zinc-600">
                    <span
                        className={`inline-flex items-center gap-1 font-medium ${
                            device.isActive && !isExpired
                                ? "text-green-600"
                                : "text-red-500"
                        }`}
                    >
                        <span
                            className={`w-2 h-2 rounded-full ${
                                device.isActive && !isExpired
                                    ? "bg-green-500"
                                    : "bg-red-400"
                            }`}
                        />
                        {device.isActive && !isExpired ? "Active" : "Expired"}
                    </span>
                    <span>Last authorized: {lastAuthorized}</span>
                    <span>Last used: {lastUsed}</span>
                    {expiresAt && (
                        <span>
                            Expires:{" "}
                            {isExpired
                                ? "Expired"
                                : expiresAt.toLocaleDateString()}
                        </span>
                    )}
                </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
                <PrimaryButton
                    onClick={() => renewMutation.mutate()}
                    disabled={renewMutation.isPending}
                >
                    <FontAwesomeIcon icon={faArrowsRotate} />
                    {renewMutation.isPending ? "Renewing..." : "Renew"}
                </PrimaryButton>
                <SecondaryButton
                    onClick={() => {
                        if (
                            window.confirm(
                                `Remove "${device.deviceName || "this device"}"? The mobile app will need to re-authenticate.`,
                            )
                        ) {
                            deleteMutation.mutate();
                        }
                    }}
                    disabled={deleteMutation.isPending}
                >
                    <FontAwesomeIcon icon={faTrashCan} />
                    {deleteMutation.isPending ? "Removing..." : "Remove"}
                </SecondaryButton>
            </div>
        </div>
    );
};

export const MobileDevicesPage = () => {
    useDocumentTitle("Mobile Devices - AERSHED");
    const apiClient = useAuthApiClient();
    const connectedDevices = useQuery({
        queryKey: ["connectedDevices"],
        queryFn: async () => {
            return await apiClient.authMobileActivateList();
        },
    });

    return (
        <div className="w-full h-screen pt-10 flex flex-col items-center">
            <div className="w-2/3">
                <div className="p-8">
                    <h1 className="text-stone-900 text-xl font-semibold leading-7">
                        Mobile Devices
                    </h1>
                    <p className="text-zinc-600 text-sm leading-snug">
                        Manage mobile devices connected with your account.
                    </p>
                </div>
                {connectedDevices.isLoading && (
                    <div className="w-full h-64 flex items-center justify-center">
                        <LoadingIndicator />
                    </div>
                )}
                {connectedDevices.data &&
                    connectedDevices.data.length === 0 && (
                        <div className="w-full h-32 flex items-center justify-center text-zinc-500 text-sm">
                            No mobile devices connected to your account.
                        </div>
                    )}
                <div className="flex flex-col gap-3">
                    {connectedDevices.data?.map((device) => (
                        <DeviceRow key={device.deviceId} device={device} />
                    ))}
                </div>
                {connectedDevices.data && connectedDevices.data.length > 0 && (
                    <div className="mt-4 px-2">
                        <p className="text-xs text-zinc-500">
                            <strong>Renew</strong> resets the token validity to
                            7 days from today. Use this if your device is about
                            to expire.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};
